import { initializeApp } from 'firebase/app'
import { getAuth, connectAuthEmulator } from 'firebase/auth'
import { connectFirestoreEmulator, initializeFirestore } from 'firebase/firestore'
import { getStorage, connectStorageEmulator } from 'firebase/storage'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'

// Environment detection
export const APP_ENV = import.meta.env.VITE_APP_ENV || 'development'
export const IS_PRODUCTION = APP_ENV === 'production'
export const IS_STAGING = APP_ENV === 'staging'
export const IS_DEVELOPMENT = APP_ENV === 'development'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

// Validate required config in production
if (IS_PRODUCTION) {
  const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'appId']
  const missingKeys = requiredKeys.filter(key => !firebaseConfig[key as keyof typeof firebaseConfig])
  if (missingKeys.length > 0) {
    console.error(`Missing required Firebase config: ${missingKeys.join(', ')}`)
  }
}

// Initialize Firebase
export const app = initializeApp(firebaseConfig)

// Initialize services
export const auth = getAuth(app)

// Initialize Firestore with long polling to avoid WebSocket connection issues
export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
})

export const storage = getStorage(app)
export const functions = getFunctions(app)

const projectId = firebaseConfig.projectId || import.meta.env.VITE_FIREBASE_PROJECT_ID

export const functionsBaseUrl =
  import.meta.env.VITE_FIREBASE_FUNCTIONS_URL ||
  (
    import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true'
      ? `http://localhost:5001/${projectId}/us-central1`
      : `https://us-central1-${projectId}.cloudfunctions.net`
  )

// Connect to emulators in development only
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true })
  connectFirestoreEmulator(db, 'localhost', 8080)
  connectStorageEmulator(storage, 'localhost', 9199)
  connectFunctionsEmulator(functions, 'localhost', 5001)
  console.log('🔧 Connected to Firebase emulators')
}

// Log environment in non-production
if (!IS_PRODUCTION) {
  console.log(`🌍 Environment: ${APP_ENV}`)
}
